<template>
    <vx-expandable-card>
        <template v-slot:header>To</template>
        <template v-slot:content>
            <div class="flex flex-col gap-3 border bg-slate-100 border-white border-solid shadow rounded-lg p-4">
                <div v-if="audiences.length > 0" class="flex items-center gap-1.5" data-test="selection-summary">
                    <font-awesome-icon
                        v-if="getAudienceType() === 'segments'"
                        :icon="faAddressBook"
                        class="text-xs text-slate-800"
                    />
                    <font-awesome-icon
                        v-if="getAudienceType() === 'external-contact-lists'"
                        :icon="faBookUser"
                        class="text-xs text-slate-800"
                    />
                    <span class="text-xs font-bold text-slate-800 leading-snug">
                        {{ audienceSummaryText }}
                    </span>
                </div>

                <vx-button @click="showAudiencesModal = true" data-test="select-audiences-button" color="muted">
                    Choose Audience
                    <font-awesome-icon widthAuto :icon="faMagnifyingGlass" />
                </vx-button>
            </div>

            <page-select-audiences-modal
                v-model:visible="showAudiencesModal"
                v-model="audiences"
                :audienceType="props.campaignType"
                :groupIds="props.groupIds"
                @confirm="handleAudiencesConfirm"
            />
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxButton, VxExpandableCard } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import PageSelectAudiencesModal from '~/components/quick-blast/PageSelectAudiencesModal.vue';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '~/constants/campaign';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faAddressBook, faBookUser, faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { pluralized } from '~/components/filters';

const props = defineProps({
    campaignType: {
        type: String,
        default: undefined,
        validator: (campaignType) =>
            [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, undefined].includes(campaignType),
    },
    groupIds: {
        type: Array,
        required: false,
    },
});

const audiences = defineModel({
    type: Array,
    required: true,
});

const showAudiencesModal = ref(false);

watch(
    () => props.campaignType,
    (newType, oldType) => {
        if (newType !== oldType && audiences.value.length > 0) {
            audiences.value = [];
        }
    }
);

const getAudienceType = () => {
    if (audiences.value.length === 0) {
        return null;
    }
    return audiences.value[0].sourceType;
};

const audienceSummaryText = computed(() => {
    if (audiences.value.length === 0) {
        return 'No audiences selected';
    }
    const type = getAudienceType();
    const label = type === 'segments' ? 'Segment' : 'External List';
    return `${pluralized(label, audiences.value.length, true)}`;
});

const handleAudiencesConfirm = (selectedAudiences) => {
    audiences.value = [...selectedAudiences];
    showAudiencesModal.value = false;
};
</script>
